import { FINISH_CODE, IntermediatePoint, RelayTeamsStarttime, Runner, RunnerStatus, PlaceAndTime, ResultLookup, Class, ShowPlaceAndTime } from "./types";

// Taken from https://stackoverflow.com/questions/12369824/javascript-binary-search-insertion-preformance
function BinarySearch(sortedRunners: PlaceAndTime[], runningUsedTime: number): number {
    let l = 0;
    let h = sortedRunners.length - 1;

    while (l <= h) {
        const m = (l + h) >>> 1; /* equivalent to Math.floor((l + h) / 2) but faster */
        const comparison = sortedRunners[m].time - runningUsedTime;
        if (comparison < 0) {
            l = m + 1;
        } else if (comparison > 0) {
            h = m - 1;
        } else {
            return m;
        }
    }

    return ~l;
}

export function CalculateRunningTime(
    runner: Runner,
    now: number,
    classIntermediatePoint: IntermediatePoint[],
    relayTeamsStarttime: RelayTeamsStarttime,
    resultLookup: ResultLookup,
    placeAndTime: ShowPlaceAndTime): void {

    for (const key in runner.times) {
        const time = runner.times[key];
        time.running_time = null;
        time.running_place = null;
        time.running_difference = null;
    }

    if (runner.status != RunnerStatus.REGISTERED) {
        return;
    }

    let stop = runner.starttime == null;
    let starttime: string = null;
    if (runner.relay_team != null && runner.relay_team != '') {
        const bibString = runner.bib.toString();
        let bibnumberLookup = bibString.substring(0, bibString.length - 2) + '01';
        starttime = relayTeamsStarttime[bibnumberLookup];
    } else {
        if (('free_start' in placeAndTime) &&
            placeAndTime.free_start) {
            // Set stop to true, so that we only continue in the case
            // where we have an estimated starttime.
            stop = true;

            if (('estimated_starttime' in runner) &&
                runner.estimated_starttime != null) {
                starttime = runner.estimated_starttime;
                stop = runner.estimated_starttime == null;
            }
        } else {
            starttime = runner.starttime;
        }
    }

    // Note that we intentionally check for runner.starttime!
    // If the runner hasn't started, we do not want to show any running clock.
    // NOTE: The above sentence only makes sense for relay.
    if (stop) {
        return;
    }

    let nextPassingCode = FINISH_CODE;
    let nextPassing = runner.times[nextPassingCode];
    for (let i = classIntermediatePoint.length - 1; i >= 0; --i) {
        if (runner.relay_team != null && runner.leg_number != classIntermediatePoint[i].leg_number) {
            continue;
        }

        if (!(classIntermediatePoint[i].code in runner.times)) {
            console.error('This should not happen');
        }

        if (runner.times[classIntermediatePoint[i].code].passing_time != null) {
            break;
        }

        nextPassingCode = classIntermediatePoint[i].code;
        nextPassing = runner.times[nextPassingCode];
    }

    let unixtime = Date.parse(starttime);
    if (now >= unixtime) {
        // Round to closest second.
        let diff = Math.round((now - unixtime) / 1000) * 1000;
        if (placeAndTime.is_pursuit && ('time_previous_race' in runner) && runner.time_previous_race != null) {
            diff += runner.time_previous_race;
        }

        if (('percentage') in runner && runner.percentage !== null) {
            diff = diff * (runner.percentage / 100.0);
        }

        let addMilliseconds = 0;
        if (('penalty' in placeAndTime)) {
            const numShootingsToAdd = NumShootingsToAddFromCode(nextPassingCode);

            let totalNumMisses = 0;
            if (('shootings' in runner) && runner.shootings !== null) {
                for (let i = 0; i < runner.shootings.length; ++i) {
                    if (numShootingsToAdd != -1 && i >= numShootingsToAdd) {
                        break;
                    }

                    const parsedShooting = Number.parseInt(runner.shootings[i]);
                    if (!Number.isNaN(parsedShooting)) {
                        totalNumMisses += parsedShooting;
                    }
                }

                addMilliseconds = placeAndTime.penalty * totalNumMisses;
            }
        }


        if (placeAndTime.show_time) {
            nextPassing.running_time = diff + addMilliseconds;
        }

        const not_competing = ('not_competing' in runner) && runner.not_competing;
        if (!not_competing) {
            let sortedRunners: PlaceAndTime[] = [];
            let legNumber = 1;
            if (runner.leg_number !== null) {
                legNumber = runner.leg_number;
            }

            if ((runner.class_id in resultLookup.classes) &&
                (legNumber in resultLookup.classes[runner.class_id].legs) &&
                (nextPassingCode in resultLookup.classes[runner.class_id].legs[legNumber].intermediate_points)) {
                sortedRunners = resultLookup.classes[runner.class_id].legs[legNumber].intermediate_points[nextPassingCode];
            }

            if (placeAndTime.show_place) {
                const index = BinarySearch(sortedRunners, diff);
                if (index < 0) {
                    nextPassing.running_place = ~index + 1;
                } else {
                    nextPassing.running_place = sortedRunners[index].place;
                }
            }

            if (sortedRunners.length > 0 && placeAndTime.show_time && placeAndTime.show_place) {
                nextPassing.running_difference = diff - sortedRunners[0].time;
            }
        }
    }
}


export function CreateResultLookup(
    classes: Class[],
    runners: Runner[]
): ResultLookup {
    let resultLookup: ResultLookup = {
        classes: {}
    };

    for (const classObj of classes) {
        resultLookup.classes[classObj.class_id] = {
            show_place: classObj.show_place,
            show_time: classObj.show_time,
            legs: {}
        };
    }

    for (const runner of runners) {
        const not_competing = ('not_competing' in runner) && runner.not_competing;
        const addToFinish = runner.status == RunnerStatus.OK && !not_competing;
        const addToIntermediate = (runner.status == RunnerStatus.REGISTERED || addToFinish) && !not_competing;

        if (!addToFinish && !addToIntermediate) {
            continue;
        }

        if (!(runner.class_id in resultLookup.classes)) {
            continue;
        }

        for (const code in runner.times) {
            const time = runner.times[code];
            if (time.passing_time === null) {
                continue;
            }

            if (code == FINISH_CODE && !addToFinish) {
                continue;
            }

            if (code != FINISH_CODE && !addToIntermediate) {
                continue;
            }

            let leg_number = 1;
            if (runner.leg_number != null) {
                leg_number = runner.leg_number;
            }

            if (!(leg_number in resultLookup.classes[runner.class_id].legs)) {
                resultLookup.classes[runner.class_id].legs[leg_number] = {
                    intermediate_points: {}
                };
            }

            if (!(code in resultLookup.classes[runner.class_id].legs[leg_number].intermediate_points)) {
                resultLookup.classes[runner.class_id].legs[leg_number].intermediate_points[code] = [];
            }

            resultLookup.classes[runner.class_id].legs[leg_number].intermediate_points[code].push({
                'place': time.place,
                'time': time.used_time
            });
        }
    }

    for (const class_id in resultLookup.classes) {
        for (const leg_number in resultLookup.classes[class_id].legs) {
            for (const code in resultLookup.classes[class_id].legs[leg_number].intermediate_points) {
                resultLookup.classes[class_id].legs[leg_number].intermediate_points[code].sort(function (a, b) {
                    return a.time - b.time;
                });
            }
        }
    }

    return resultLookup;
}


export function NumShootingsToAddFromCode(code: string) {
    // 66 is pre shooting
    // 70 is post shooting
    // 71 is pre-finish
    const codeStr = code.toString();
    if (codeStr.startsWith('66')) {
        return Number.parseInt(codeStr.substring(2)) - 1;
    }

    if (codeStr.startsWith('70')) {
        return Number.parseInt(codeStr.substring(2));
    }

    if (codeStr.startsWith('71')) {
        return -1;
    }

    if (codeStr == FINISH_CODE) {
        return -1;
    }

    return 0;
}
